import { mutationOptions, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { ApiError } from '@/shared/api/http-client';

import type {
    CancelPlanInput,
    CreateCheckoutSessionInput,
    UpdatePlanInput,
} from '../model/subscription.types';
import { type PortalTarget, subscriptionApi } from './subscription.api';
import { queryKeys } from './subscription.queries';

export const mutationKeys = {
    all: ['subscription'] as const,
    cancelPlan: () => [...mutationKeys.all, 'cancel-plan'] as const,
    reactivate: () => [...mutationKeys.all, 'reactivate'] as const,
};

export function createPortalSessionOptions() {
    return mutationOptions({
        mutationFn: (target?: PortalTarget) =>
            subscriptionApi.createPortalSession(window.location.href, target),
        onSuccess: ({ url }) => {
            window.location.href = url;
        },
        onError: () => {
            toast.error('Something went wrong. Please try again.');
        },
    });
}

export function useReactivateSubscriptionOptions() {
    const queryClient = useQueryClient();
    return mutationOptions({
        mutationKey: mutationKeys.reactivate(),
        mutationFn: () => subscriptionApi.reactivate(),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.me() });
        },
        onError: (error) => {
            if (error instanceof ApiError && error.status === 409) {
                toast.error("This plan can't be reactivated right now — please refresh");
                return;
            }
            toast.error('Something went wrong. Please try again.');
        },
    });
}

export function createCheckoutSessionOptions() {
    return mutationOptions({
        mutationFn: (input: CreateCheckoutSessionInput) =>
            subscriptionApi.createCheckoutSession(input),
        onSuccess: ({ url }) => {
            window.location.href = url;
        },
        onError: () => {
            toast.error('Something went wrong. Please try again.');
        },
    });
}

export function useUpdatePlanOptions() {
    const queryClient = useQueryClient();
    return mutationOptions({
        mutationFn: (input: UpdatePlanInput) => subscriptionApi.updatePlan(input),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.me() });
        },
    });
}

export function useCancelPlanOptions() {
    const queryClient = useQueryClient();
    return mutationOptions({
        mutationKey: mutationKeys.cancelPlan(),
        mutationFn: (input: CancelPlanInput) => subscriptionApi.cancelPlan(input),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.me() });
        },
    });
}
